/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Typography, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DateTime } from 'luxon';
import { UserAvatar } from 'components/elements/UserAvatar';
import { dateFormats } from 'const';
import { dateUtils } from 'utils';
import { NameLabel } from 'components/elements';

import Styles from './styles';

const useStyles = makeStyles(Styles);

function Message({ isActive, isRead, body, updatedAt, onSelectMessage, participants, reOpenButton, onReopenConversation }) {
  const classes = useStyles();
  const messageClasses = cn(classes.message, { [classes.selectedMessage]: isActive }, { [classes.unreadMessage]: !isRead && !isActive });
  const { i18n } = useTranslation();
  const { t } = useTranslation('labels');
  const params = useMemo(
    () => ({
      locale: i18n.language,
      format: dateFormats[i18n.language] || 'MMMM dd, yyyy',
    }),
    [i18n.language],
  );
  // display only first line of multiline message.
  const messagePreview = body.split('&#13;\n')[0];

  const conversationProviders = useMemo(() => {
    let pcp = [];
    if (participants) {
      pcp = participants.filter(({ role }) => role === 'provider') || [];
    }

    return uniqBy(pcp, 'id');
  }, [participants]);

  const currentDateTime = DateTime.now().toFormat(params.format);
  const dateTimeUpdated = DateTime.fromISO(updatedAt).toFormat(params.format);
  const timeUpdated = dateUtils.formatTimeString(updatedAt);
  const numberOfProvider = conversationProviders.length;
  const includePronoun = numberOfProvider === 1;

  return (
    <div className={messageClasses} onClick={onSelectMessage} data-testid="message">
      <div className={classes.messageWrapper}>
        <div className={classes.userAvatar}>
          <UserAvatar participants={conversationProviders} />
        </div>
        <div className={classes.bodyWrapper}>
          <Typography className={classes.heading} variant="subtitle1" component="p" noWrap>
            {conversationProviders.map(({ fullName, personalPronoun }, i) => (
              <NameLabel
                fullName={fullName}
                personalPronoun={includePronoun ? personalPronoun : null}
                delimiter={numberOfProvider - 1 === i ? '' : ', '}
                options={{ pronoun: { parentheses: true, mutedText: true } }}
              />
            ))}
          </Typography>
          <Typography className={classes.body} variant="body2" color="textSecondary" component="p" noWrap>
            {messagePreview}
          </Typography>
        </div>
        {reOpenButton && (
          <Button variant="contained" color="warning" onClick={onReopenConversation} className={classes.reOpenButton}>
            <Typography variant="caption" component="span" className={classes.reOpenButtonText}>
              {t('reopen')}
            </Typography>
          </Button>
        )}
        <p className={classes.date}>{currentDateTime === dateTimeUpdated ? timeUpdated : dateTimeUpdated}</p>
      </div>
    </div>
  );
}

Message.propTypes = {
  isActive: PropTypes.bool.isRequired,
  isRead: PropTypes.bool.isRequired,
  body: PropTypes.string.isRequired,
  updatedAt: PropTypes.string.isRequired,
  onSelectMessage: PropTypes.func.isRequired,
  participants: PropTypes.arrayOf(PropTypes.object).isRequired,
  reOpenButton: PropTypes.bool.isRequired,
  onReopenConversation: PropTypes.func.isRequired,
};

export default Message;
